import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CheckExistence.module.scss';
import LabeledInput from './components/LabeledInput';
import { SUPPORTED_ID_CHECK_DOM } from './supportedFormAutofill';
import Alert from '@/components/ui/Alert';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import searchSupportedIc from '@/assets/icons/search-supported.svg';
import arrowLeftIc from '@/assets/icons/arrow-left.svg';
import arrowLeftDisabledIc from '@/assets/icons/arrow-left-disabled.svg';
import doneIc from '@/assets/icons/done.svg';
import done2Ic from '@/assets/icons/done2.svg';
import info2Ic from '@/assets/icons/info2.svg';
import alertIc from '@/assets/icons/alert2.svg';
import idIc from '@/assets/icons/id.svg';
import idDisabledIc from '@/assets/icons/id-disabled.svg';
import { SupportedFullInfoWithConnectionToFundFlagDtoIn } from '@/models/in/SupportedFullInfoWithConnectionToFundFlagDtoIn';
import useFundContext from '@/contexts/Fund/useFundContext';
import { SupportedFullInfoDtoIn } from '@/models/in/SupportedFullInfoDtoIn';
import { usePostAuthApi, usePostAuthApiQuery } from '@/hooks/UseApiCalls';
import i18n from '@/i18n/i18n';

function translateKnownErrorCode(errorCode: unknown, t: (key: string) => string, fallbackKey: string): string {
  if (typeof errorCode === 'string' && i18n.exists(errorCode)) {
    return t(errorCode);
  }
  return t(fallbackKey);
}

interface CheckExistenceProps {
  start: (supported: SupportedFullInfoDtoIn | null, israeliId: string) => void;
  openSupportedAdditionalInfo?: (supportedId: number) => Promise<void>;
}

const CheckExistence = ({ start, openSupportedAdditionalInfo }: CheckExistenceProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const [inputIsraeliId, setInputIsraeliId] = useState<string>('');
  const [searchedIsraeliId, setSearchedIsraeliId] = useState<string>('');
  const [isConnectedToFund, setIsConnectedToFund] = useState<boolean>(false);
  const [supported, setSupported] = useState<SupportedFullInfoDtoIn | null>(null);
  const [israeliIdValidationError, setIsraeliIdValidationError] = useState<string>('');
  const [idFormatHint, setIdFormatHint] = useState('');
  const idFormatHintId = `${SUPPORTED_ID_CHECK_DOM.id}-format-hint`;

  // The check digit failing is a WARNING, not a gate: the system itself holds contacts whose id
  // does not pass it (legacy FormsSupporteds rows, Donex imports, contacts created through the
  // fund-representative / management paths), and blocking the lookup made those records
  // unreachable from this screen. We still look them up - we only refuse to CREATE a new
  // supported from a number that fails the check (HAT-1341).
  //
  // Derived from the id that was actually SEARCHED, never from the field: everything else on
  // this screen keeps showing the last search while the user edits, and a flag that reset on
  // every keystroke would re-enable Start against that stale - invalid - result.
  const searchedIdFailsCheckDigit = !!searchedIsraeliId && !isValidIsraeliID(searchedIsraeliId);

  // The id goes in the POST body, never the URL: a query string ends up in server and proxy logs
  // and in the browser's history (WOR-1423).
  const url = searchedIsraeliId && currentFund?.id ? 'supporteds/lookup-with-fund-connection' : '';
  const lookupBody = { israeliId: searchedIsraeliId, fundId: currentFund?.id };

  const {
    data: result,
    isSuccess,
    isError,
    error,
    refetch,
    isFetching,
    isRefetching,
  } = usePostAuthApiQuery<SupportedFullInfoWithConnectionToFundFlagDtoIn, typeof lookupBody>(url, lookupBody, { retry: false, staleTime: 0 });
  const isLookingUp = isFetching || isRefetching;
  const startButtonRef = useRef<HTMLButtonElement>(null);
  const checkButtonRef = useRef<HTMLButtonElement>(null);
  const connectErrorRef = useRef<HTMLSpanElement>(null);
  const showContactButtonRef = useRef<HTMLButtonElement>(null);
  const addToFundButtonRef = useRef<HTMLButtonElement>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const [highlightStart, setHighlightStart] = useState(false);
  const [lookupAnnouncement, setLookupAnnouncement] = useState('');
  const [connectLiveMessage, setConnectLiveMessage] = useState('');
  const [lookupNonce, setLookupNonce] = useState(0);
  const wasLookingUpRef = useRef(false);
  const lastLookupTriggerRef = useRef<'enter' | 'click' | null>(null);

  const lookupResponseData = (error as { response?: { data?: { errorCode?: string } | string } })?.response?.data;
  const lookupErrorCode =
    lookupResponseData && typeof lookupResponseData === 'object' && typeof lookupResponseData.errorCode === 'string' ? lookupResponseData.errorCode : undefined;
  const lookupErrorMessage = isError ? translateKnownErrorCode(lookupErrorCode, t, 'errorLookingUpSupported') : '';

  const clearHighlightTimer = () => {
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    setHighlightStart(false);
  };

  const clearLookupResult = () => {
    setSearchedIsraeliId('');
    setSupported(null);
    setIsConnectedToFund(false);
    setLookupAnnouncement('');
    setConnectLiveMessage('');
    setConnectErrorMessage('');
    setIdFormatHint('');
    clearHighlightTimer();
  };

  useEffect(() => () => clearHighlightTimer(), []);

  const connectUrl = supported?.contact.id && currentFund?.id ? `funds/${currentFund.id}/supporteds/${supported.contact.id}/connect-to-fund` : '';
  const [connectErrorMessage, setConnectErrorMessage] = useState('');
  const {
    mutate: connectToFund,
    isPending: isConnecting,
    isSuccess: isConnectSuccess,
  } = usePostAuthApi<void, object>(connectUrl, {
    onSuccess: () => {
      setConnectErrorMessage('');
      setConnectLiveMessage(t('contactAddedToFund'));
    },
    onError: (error: unknown) => {
      const responseData = (error as { response?: { data?: { errorCode?: string } } })?.response?.data;
      const message = translateKnownErrorCode(responseData?.errorCode, t, 'errorAddingSupportedToFund');
      setConnectErrorMessage(message);
      setConnectLiveMessage(message);
    },
  });

  useEffect(() => {
    if (isConnectSuccess) {
      setIsConnectedToFund(true);
    }
  }, [isConnectSuccess]);

  useEffect(() => {
    if (result) {
      if (!result.supportedFullInfo) {
        setSupported(null);
        setIsConnectedToFund(false);
      } else {
        setIsConnectedToFund(result.isSupportedOfFund);
        setSupported(result.supportedFullInfo);
      }
    }
  }, [result]);

  const normalizedInputIsraeliId = inputIsraeliId ? inputIsraeliId.padStart(9, '0') : '';
  const idSearchMatchesInput = !!searchedIsraeliId && searchedIsraeliId === normalizedInputIsraeliId;
  const foundContact = idSearchMatchesInput && isSuccess ? (result?.supportedFullInfo ?? null) : null;
  const found = !!foundContact;
  const connectedInFund = found && !!result?.isSupportedOfFund;
  /*
   * HAT-1464: a supported the office deleted - usually over wrong details. Connecting them as they
   * are would bring the wrong details straight back, so instead Start opens the form on their record,
   * ready to correct. The ID itself is theirs already: the check digit does not gate this.
   */
  const foundDeleted = found && !!foundContact?.contact.isArchived && !connectedInFund;

  const canProceedToStart =
    idSearchMatchesInput &&
    isSuccess &&
    !isLookingUp &&
    (foundDeleted || (!found && !searchedIdFailsCheckDigit)) &&
    !connectedInFund &&
    !isConnectedToFund &&
    !isConnectSuccess;

  useEffect(() => {
    if (!canProceedToStart) {
      clearHighlightTimer();
    }
  }, [canProceedToStart]);

  useEffect(() => {
    if (isLookingUp || !searchedIsraeliId || lookupNonce <= 0) {
      wasLookingUpRef.current = isLookingUp;
      return;
    }

    if (isError) {
      setLookupAnnouncement(lookupErrorMessage || t('errorLookingUpSupported'));
      if (lastLookupTriggerRef.current === 'click') {
        checkButtonRef.current?.focus();
      } else if (lastLookupTriggerRef.current === 'enter') {
        (document.getElementById(SUPPORTED_ID_CHECK_DOM.id) as HTMLInputElement | null)?.focus();
      }
    } else if (isSuccess) {
      const contact = foundContact?.contact;
      if (!found && !searchedIdFailsCheckDigit) {
        setLookupAnnouncement(t('WeCouldntFindAnExistingContactWithTheIDNumberYouEntered'));
      } else if (found && contact) {
        setLookupAnnouncement(
          foundDeleted
            ? `${t('reenterDeletedSupportedFound')} ${contact.firstName} ${contact.lastName}. ${t('reenterDeletedSupportedExplanation')}`
            : `${t('YesIdentifiedAnExistingContactInTheSystemWithTheSameIDNumberUnderTheName')} ${contact.firstName} ${contact.lastName}`,
        );
      } else if (searchedIdFailsCheckDigit) {
        // Check-digit copy lives in the Alert; avoid duplicating in sr-only.
        setLookupAnnouncement('');
      } else {
        setLookupAnnouncement(t('checkIsraeliIdComplete'));
      }

      if (canProceedToStart) {
        clearHighlightTimer();
        setHighlightStart(true);
        highlightTimerRef.current = window.setTimeout(() => {
          setHighlightStart(false);
          highlightTimerRef.current = null;
        }, 2500);
        if (lastLookupTriggerRef.current === 'enter') {
          (document.getElementById(SUPPORTED_ID_CHECK_DOM.id) as HTMLInputElement | null)?.focus();
        } else {
          startButtonRef.current?.focus();
        }
      } else if (lastLookupTriggerRef.current === 'click' && (found || searchedIdFailsCheckDigit)) {
        checkButtonRef.current?.focus();
      } else if (lastLookupTriggerRef.current === 'enter') {
        (document.getElementById(SUPPORTED_ID_CHECK_DOM.id) as HTMLInputElement | null)?.focus();
      }
    }

    wasLookingUpRef.current = isLookingUp;
    if (!isLookingUp) {
      lastLookupTriggerRef.current = null;
    }
  }, [
    isLookingUp,
    isSuccess,
    isError,
    found,
    foundDeleted,
    foundContact,
    result,
    searchedIsraeliId,
    searchedIdFailsCheckDigit,
    canProceedToStart,
    lookupNonce,
    lookupErrorMessage,
    t,
  ]);

  // Never drop a keystroke or a paste silently. Users paste ids out of Excel and mail, where
  // they carry spaces, dashes and the odd invisible character; the previous version rejected
  // the whole change in that case, so the field simply stayed empty - or, when a tenth digit
  // was typed, quietly kept the first nine and then called the result invalid (HAT-1341).
  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    const next = digits.slice(0, 9);
    if (searchedIsraeliId && next.padStart(9, '0') !== searchedIsraeliId) {
      clearLookupResult();
    }
    setInputIsraeliId(next);
    setIsraeliIdValidationError(digits.length > 9 ? t('israeliIdTooLong') : '');
    setIdFormatHint('');
  };

  const handleIdBlur = () => {
    // After a lookup, the Alert / result panel owns validation copy — blur must not inject a hint (H3).
    if (idSearchMatchesInput) {
      return;
    }
    const trimmed = inputIsraeliId.trim();
    if (!trimmed) {
      return;
    }
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length >= 7 && !isValidIsraeliID(digits)) {
      setIdFormatHint(t('israeliIdCheckDigitFailed'));
    }
    // Never clear hint on blur (H1): clearing changes status text and steals focus in the dialog.
  };

  const idFieldHint = israeliIdValidationError || idFormatHint;
  const idFieldAriaInvalid = Boolean(idFormatHint || (israeliIdValidationError && inputIsraeliId && !isValidIsraeliID(inputIsraeliId)));

  const checkSupportedExists = () => {
    if (isLookingUp) {
      return;
    }

    if (!inputIsraeliId) {
      setIsraeliIdValidationError(t('israeliIdRequired'));
      return;
    }

    // Shorter ids are real - they are simply written without their leading zeros. The server
    // pads on lookup too, so pad here as well and search for the canonical 9 digit form.
    const normalizedIsraeliId = inputIsraeliId.padStart(9, '0');

    setIsraeliIdValidationError('');
    setIdFormatHint('');
    setLookupAnnouncement('');
    clearHighlightTimer();
    setLookupNonce((n) => n + 1);

    if (searchedIsraeliId === normalizedIsraeliId) {
      refetch();
    } else {
      setSupported(null);
      setIsConnectedToFund(false);

      setSearchedIsraeliId(normalizedIsraeliId); // This triggers the API call
    }
  };

  useEffect(() => {
    if (!isConnectSuccess) return;
    const timer = window.setTimeout(() => {
      showContactButtonRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isConnectSuccess]);

  useEffect(() => {
    if (!connectErrorMessage || isConnecting) return;
    const timer = window.setTimeout(() => {
      connectErrorRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [connectErrorMessage, isConnecting]);

  const handleStartClick = () => {
    if (foundContact) {
      start(foundContact, searchedIsraeliId);
    } else {
      start(null, searchedIsraeliId);
    }
  };

  return (
    <>
      <div className={styles.checkExistenceContainer}>
        <img src={searchSupportedIc} alt="Search Supported Icon" className="mb-8 max-md:mb-4" />

        <span className="body-lr-medium text-(--font-heading) mb-1 max-md:text-center">{t('beforeStarting')}</span>
        <span className="heading-h3-bold text-(--font-heading) mb-8 max-md:mb-4 max-md:text-center">
          {t('checkIfContactDetailsExistInTheMercazHatzedakaSystem')}
        </span>

        <div className="w-full max-w-[412px]">
          <div className="mb-4">
            <div className="flex items-end gap-4">
              <div className="w-full">
                <LabeledInput
                  label="enterNewSupportedIsrealiId"
                  id={SUPPORTED_ID_CHECK_DOM.id}
                  name={SUPPORTED_ID_CHECK_DOM.name}
                  autoComplete="off"
                  iconSrc={idIc}
                  disabledIconSrc={idDisabledIc}
                  iconAlt="ID Icon"
                  type="text"
                  inputMode="numeric"
                  value={inputIsraeliId}
                  onChange={handleIdChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !isLookingUp) {
                      lastLookupTriggerRef.current = 'enter';
                      checkSupportedExists();
                    }
                  }}
                  onBlur={handleIdBlur}
                  fieldHint={idFieldHint || undefined}
                  fieldHintId={idFormatHintId}
                  fieldAriaInvalid={idFieldAriaInvalid}
                  showFieldHint={false}
                  placeholder="israeliIdNumber"
                />
              </div>
              <button
                ref={checkButtonRef}
                type="button"
                className={`${styles.checkButton} ${isLookingUp ? styles.checkButtonLoading : ''}`}
                onClick={() => {
                  if (isLookingUp || !inputIsraeliId.trim()) return;
                  lastLookupTriggerRef.current = 'click';
                  checkSupportedExists();
                }}
                disabled={!inputIsraeliId.trim()}
                aria-disabled={!inputIsraeliId.trim() || isLookingUp}
                aria-label={t('checkIsraeliId')}
                aria-busy={isLookingUp}
              >
                {isLookingUp ? (
                  <span className={styles.checkButtonSpinner} aria-hidden="true" />
                ) : (
                  <img src={inputIsraeliId.trim() ? arrowLeftIc : arrowLeftDisabledIc} alt="" />
                )}
              </button>
            </div>
            <p id={idFormatHintId} className={`body-sm-regular ${styles.idFieldHintSlot}`} role="status" aria-live="polite">
              {idFieldHint}
            </p>
          </div>

          <div className="sr-only" aria-live="polite" role="status">
            {connectLiveMessage || lookupAnnouncement}
          </div>

          {/* ONE notice for one situation.
              It used to be three, all on screen together: a warning that the check digit does not
              match, a green tick reading "we found no contact", and a red line saying a supported
              cannot be created. One state, one thing the user can do about it - and the green tick
              was the worst of the three, a success mark sitting against a rejection (HAT-1341).

              The second line is the consequence, and it depends on what the lookup found: when a
              contact WAS found the record is reachable and connecting it to the fund is still
              open, so "a new supported cannot be opened" would answer a question nobody asked. */}
          {idSearchMatchesInput && searchedIdFailsCheckDigit && (
            <Alert
              type="warning"
              dismissible={false}
              title={t('israeliIdCheckDigitFailed')}
              body={t(isSuccess && found ? 'israeliIdCheckDigitRecheck' : 'israeliIdCheckDigitFailedCannotCreate')}
              className="mb-1"
            />
          )}

          {idSearchMatchesInput && isSuccess && foundDeleted && (
            <div className={styles.supportedExistsContainer}>
              <img src={info2Ic} alt="" className="pt-1" />
              <div className="flex flex-col gap-1">
                <span className={styles.supportedExistsMessage}>{t('reenterDeletedSupportedFound')}</span>
                <span className="body-md-bold text-(--font-body-5)">
                  {foundContact?.contact.firstName} {foundContact?.contact.lastName}
                </span>
                <span className="body-sm-regular text-(--font-body-2)">{t('reenterDeletedSupportedExplanation')}</span>
              </div>
            </div>
          )}

          {idSearchMatchesInput && isSuccess && found && !foundDeleted && !connectedInFund && !isConnectSuccess && (
            <>
              <div className={styles.supportedExistsContainer}>
                <img src={done2Ic} alt="Done Icon" className="pt-1" />
                <div className="flex flex-col">
                  <span className={styles.supportedExistsMessage}>{t('YesIdentifiedAnExistingContactInTheSystemWithTheSameIDNumberUnderTheName')}</span>
                  <span className="body-md-bold text-(--font-body-5)">
                    {foundContact?.contact.firstName} {foundContact?.contact.lastName}
                  </span>
                </div>
              </div>

              {connectErrorMessage && !isConnecting && (
                <div className="flex items-center gap-2 py-2" role="alert">
                  <img src={alertIc} alt="" className="w-5 h-5" />
                  <span ref={connectErrorRef} tabIndex={-1} className={`text-(--status-rejected-text) ${styles.connectErrorFocusTarget}`}>
                    {connectErrorMessage}
                  </span>
                </div>
              )}
              {!connectErrorMessage && !isConnecting && (
                <div className="flex items-start gap-2 py-[2px]">
                  <img src={info2Ic} alt="Info Icon" />
                  <span className="body-sm-regular text-(--font-body-2)">
                    {t('youCanAddExistingContactToFund', {
                      name: `${foundContact?.contact.firstName} ${foundContact?.contact.lastName}`,
                    })}
                  </span>
                </div>
              )}

              <button
                ref={addToFundButtonRef}
                type="button"
                onClick={() => connectToFund({})}
                disabled={isConnecting}
                className="body-md-medium text-(--font-info) underline text-start"
              >
                {isConnecting ? t('adding') : t('addContactAsSupportedToFund')}
              </button>
            </>
          )}

          {idSearchMatchesInput && isSuccess && found && isConnectSuccess && (
            <>
              <div className="flex flex-col gap-4" role="status">
                <span className="body-md-medium text-(--font-body-5)">{t('contactAddedToFund')}</span>

                <button
                  ref={showContactButtonRef}
                  type="button"
                  onClick={async () => {
                    if (foundContact?.contact.id && openSupportedAdditionalInfo) {
                      try {
                        await openSupportedAdditionalInfo(foundContact.contact.id);
                      } catch {
                        alert(t('errorOpeningSupportedAdditionalInfo'));
                      }
                    }
                  }}
                  className={`body-md-semibold w-full text-(--font-info) underline text-start ${styles.showContactButton}`}
                >
                  {t('showContact')}
                </button>

                <span className="body-sm-regular text-(--font-body-2)">{t('incorrectSupportedDetailsNote')}</span>
              </div>
            </>
          )}

          {idSearchMatchesInput && isSuccess && found && connectedInFund && !isConnectSuccess && (
            <div className="bg-(--surface-grey-low-2) flex items-center px-4 py-[10px] rounded-lg">
              <p className="body-md-medium text-(--font-body-5)">
                <span>{t('theSupported')} </span>
                <button
                  type="button"
                  onClick={async () => {
                    if (foundContact?.contact.id && openSupportedAdditionalInfo) {
                      try {
                        await openSupportedAdditionalInfo(foundContact.contact.id);
                      } catch {
                        alert(t('errorOpeningSupportedAdditionalInfo'));
                      }
                    }
                  }}
                  className="font-bold underline cursor-pointer hover:opacity-80"
                >
                  {foundContact?.contact.firstName} {foundContact?.contact.lastName}
                </button>
                <span className="font-normal"> {t('alreadyExistsInFund')}</span>
              </p>
            </div>
          )}

          {/* "Nothing found" is good news only when the number is one we can go on to use. With a
              failed check digit it is a tick against a dead end, and the notice above has already
              said everything there is to say. */}
          {idSearchMatchesInput && isSuccess && !found && !searchedIdFailsCheckDigit && (
            <div className="flex items-center gap-2 py-[2px]">
              <img src={doneIc} alt="Done Icon" />
              <span className="body-md-medium text-(--font-tertiary)">{t('WeCouldntFindAnExistingContactWithTheIDNumberYouEntered')}</span>
            </div>
          )}

          {idSearchMatchesInput && isError && lookupErrorMessage && (
            <div className="flex items-center gap-2 py-2">
              <img src={alertIc} alt="" className="w-5 h-5" />
              <span className="text-(--status-rejected-text)">{lookupErrorMessage}</span>
            </div>
          )}
        </div>
      </div>
      <div className={styles.checkExistenceFooter}>
        {/* searchedIdFailsCheckDigit keeps CREATION strict while the lookup above stays permissive. */}
        <button
          ref={startButtonRef}
          type="button"
          className={`${styles.startButton} ${highlightStart ? styles.startButtonHighlight : ''}`}
          onClick={handleStartClick}
          disabled={!canProceedToStart}
        >
          <span className={`heading-h4-semibold ${styles.startButtonLabel}`}>{t('start')}</span>
        </button>
      </div>
    </>
  );
};

export default CheckExistence;
